import {
  Avatar, Badge, Button, Card, DateInput, EmailInput, ErrorState, Field, Input, InvitationIssuedPanel, LoadingState,
  Notice, Permissions, PhoneInput, Row, Select, Stack, Textarea, invalidDateIn, normalisePhone, useApi, useI18n,
  usePermission,
  type GuardianLinkResponse, type GuardianRelationship, type InvitationIssuedResponse, type UpdateGuardianLinkRequest,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { AccountRecoveryPanel } from './AccountRecoveryPanel';

const RELATIONSHIPS: GuardianRelationship[] = [
  'Mother', 'Father', 'Grandmother', 'Grandfather', 'Aunt', 'Uncle', 'Sibling', 'LegalGuardian', 'Other',
];

/** Where a new guardian's window opens (Q28): today by default, since what someone has seen cannot be unseen. */
type HistoryChoice = 'today' | 'enrolment' | 'date';

/**
 * A child's guardians, with adding one, re-sending an invitation, and managing each link (FR-CHD-04, FR-AUTH-15,
 * Q28, Q30).
 *
 * Adding a guardian invites them in the same step. The link goes to their phone by SMS and also comes back
 * here once, for the person adding them to copy if the text does not arrive. A guardian who has not
 * accepted can be sent a new link, which stops every earlier one working (decision 2026-09-23 #16).
 *
 * A link opens the child's history from a chosen day, and can be suspended or ended. Neither deletes it: an
 * ended link is listed under former guardians, as the record that the person was one.
 */
export function GuardiansCard({ childId, enrollmentDate }: { childId: number; enrollmentDate: string }) {
  const api = useApi();
  const { t, formatPhone, formatDate } = useI18n();
  const queryClient = useQueryClient();
  const canUpdate = usePermission(Permissions.children.update);
  const canRecover = usePermission(Permissions.accounts.recover);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [showEnded, setShowEnded] = useState(false);
  const [recovering, setRecovering] = useState<{ membershipId: number; fullName: string | null } | null>(null);
  const [issued, setIssued] = useState<InvitationIssuedResponse | null>(null);

  const guardians = useQuery({
    queryKey: ['child', childId, 'guardians', showEnded],
    queryFn: () => api.guardians.forChild(childId, showEnded),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['child', childId, 'guardians'] });

  const resend = useMutation({
    mutationFn: (linkId: number) => api.guardians.resendInvitation(childId, linkId),
    onSuccess: (invitation) => setIssued(invitation),
  });

  const standing = guardians.data?.filter((link) => !link.endedAt) ?? [];
  const ended = guardians.data?.filter((link) => link.endedAt) ?? [];

  return (
    <Card
      title={t('guardians.title')}
      action={canUpdate && !adding
        ? <Button variant="secondary" onClick={() => { setIssued(null); setAdding(true); }}>{t('guardians.add')}</Button>
        : undefined}
    >
      <Stack>
        {issued && <InvitationIssuedPanel invitation={issued} onDismiss={() => setIssued(null)} />}

        {adding && (
          <AddGuardianForm
            childId={childId}
            enrollmentDate={enrollmentDate}
            onCancel={() => setAdding(false)}
            onAdded={(invitation) => {
              setAdding(false);
              setIssued(invitation);
              void refresh();
            }}
          />
        )}

        {recovering && (
          <AccountRecoveryPanel
            membershipId={recovering.membershipId}
            fullName={recovering.fullName}
            onClose={() => setRecovering(null)}
          />
        )}

        {resend.isError && <ErrorState error={resend.error} />}
        {guardians.isPending && <LoadingState />}
        {guardians.isError && <ErrorState error={guardians.error} onRetry={() => void guardians.refetch()} />}
        {guardians.data && standing.length === 0 && <p className="dg-card__hint">{t('guardians.empty')}</p>}

        {standing.map((link) => (
          <Stack key={link.id}>
            <Row between>
              <span className="dg-stack dg-stack--tight">
                <span className="dg-row">
                  <Avatar name={link.fullName} photoUrl={link.photoUrl} size="sm" />
                  <strong>{link.fullName ?? '—'}</strong>
                </span>
                <span className="dg-row">
                  <span className="dg-card__hint">{t(`guardians.relationship.${link.relationship}`)}</span>
                  {link.isPrimary && <Badge>{t('guardians.primary')}</Badge>}
                  {link.accessLevel === 'Suspended' && <Badge tone="danger">{t('guardians.suspended')}</Badge>}
                  {link.status !== 'Active' && (
                    <Badge tone={link.status === 'Invited' ? 'info' : 'neutral'}>{t(`status.${link.status}`)}</Badge>
                  )}
                  {link.numberReleased && <Badge tone="neutral">{t('people.numberReleased')}</Badge>}
                </span>
                {link.historyVisibleFrom && (
                  <span className="dg-card__hint">
                    {t('guardians.historyFrom.shown', { date: formatDate(link.historyVisibleFrom) })}
                  </span>
                )}
                {link.accessLevel === 'Suspended' && link.restrictionNote && (
                  <span className="dg-card__hint">{link.restrictionNote}</span>
                )}
              </span>
              <span className="dg-row">
                {link.phone && <a href={`tel:${link.phone}`} dir="ltr">{formatPhone(link.phone)}</a>}
                {canRecover && !link.numberReleased && (
                  <Button
                    variant="ghost"
                    onClick={() => setRecovering({ membershipId: link.guardianMembershipId, fullName: link.fullName })}
                  >
                    {t('accountRecovery.action')}
                  </Button>
                )}
                {canUpdate && link.status === 'Invited' && (
                  <Button
                    variant="ghost"
                    busy={resend.isPending && resend.variables === link.id}
                    onClick={() => { setIssued(null); resend.mutate(link.id); }}
                  >
                    {t('invitation.resend')}
                  </Button>
                )}
                {canUpdate && editing !== link.id && (
                  <Button variant="ghost" onClick={() => setEditing(link.id)}>{t('guardians.edit')}</Button>
                )}
              </span>
            </Row>

            {canUpdate && editing === link.id && (
              <LinkEditor
                childId={childId}
                enrollmentDate={enrollmentDate}
                link={link}
                onClose={() => setEditing(null)}
                onSaved={() => { setEditing(null); void refresh(); }}
              />
            )}
          </Stack>
        ))}

        {canUpdate && (
          <Row>
            <Button variant="ghost" onClick={() => setShowEnded((shown) => !shown)}>
              {showEnded ? t('guardians.ended.hide') : t('guardians.ended.show')}
            </Button>
          </Row>
        )}

        {showEnded && guardians.data && ended.length === 0 && (
          <p className="dg-card__hint">{t('guardians.ended.empty')}</p>
        )}

        {showEnded && ended.map((link) => (
          <span key={link.id} className="dg-stack dg-stack--tight">
            <span className="dg-row">
              <strong>{link.fullName ?? '—'}</strong>
              <span className="dg-card__hint">{t(`guardians.relationship.${link.relationship}`)}</span>
            </span>
            <span className="dg-card__hint">
              {t(`guardians.ended.${link.endedReason ?? 'Unlinked'}`, { date: formatDate(link.endedAt!) })}
            </span>
          </span>
        ))}
      </Stack>
    </Card>
  );
}

/** The first readable day a choice stands for, `YYYY-MM-DD`; '' while a chosen date is not a whole one. */
function historyFrom(choice: HistoryChoice, date: string, enrollmentDate: string, today: string): string {
  if (choice === 'enrolment') return enrollmentDate < today ? enrollmentDate : today;
  if (choice === 'date') return date;
  return today;
}

function HistoryFromField({ choice, onChoice, date, onDate, today }: {
  choice: HistoryChoice;
  onChoice: (choice: HistoryChoice) => void;
  date: string;
  onDate: (date: string) => void;
  today: string;
}) {
  const { t } = useI18n();

  return (
    <>
      <Field label={t('guardians.historyFrom')} hint={t('guardians.historyFrom.hint')} required>
        {(props) => (
          <Select {...props} value={choice} onChange={(event) => onChoice(event.target.value as HistoryChoice)}>
            <option value="today">{t('guardians.historyFrom.today')}</option>
            <option value="enrolment">{t('guardians.historyFrom.enrolment')}</option>
            <option value="date">{t('guardians.historyFrom.date')}</option>
          </Select>
        )}
      </Field>
      {choice === 'date' && (
        <Field label={t('guardians.historyFrom.date')} required>
          {(props) => <DateInput {...props} value={date} onChange={onDate} max={today} />}
        </Field>
      )}
    </>
  );
}

function AddGuardianForm({ childId, enrollmentDate, onCancel, onAdded }: {
  childId: number;
  enrollmentDate: string;
  onCancel: () => void;
  onAdded: (invitation: InvitationIssuedResponse | null) => void;
}) {
  const api = useApi();
  const { t, today } = useI18n();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [relationship, setRelationship] = useState<GuardianRelationship>('Mother');
  const [isPrimary, setIsPrimary] = useState(false);
  const [historyChoice, setHistoryChoice] = useState<HistoryChoice>('today');
  const [historyDate, setHistoryDate] = useState('');
  const [errors, setErrors] = useState<{ fullName?: string; phone?: string }>({});

  const add = useMutation({
    mutationFn: (normalised: string) => api.guardians.add(childId, {
      fullName: fullName.trim(),
      phone: normalised,
      relationship,
      isPrimary,
      email: email.trim() || null,
      historyVisibleFrom: historyFrom(historyChoice, historyDate, enrollmentDate, today()),
    }),
    onSuccess: (result) => onAdded(result.invitation),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (invalidDateIn(event.currentTarget)) return;
    const normalised = normalisePhone(phone);
    const next = {
      ...(fullName.trim() ? {} : { fullName: t('guardians.name.required') }),
      ...(normalised ? {} : { phone: t('auth.phone.required') }),
    };
    setErrors(next);
    if (historyChoice === 'date' && !historyDate) return;
    if (normalised && fullName.trim()) add.mutate(normalised);
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack>
        {add.isError && <ErrorState error={add.error} />}

        <Field label={t('guardians.name')} error={errors.fullName} required>
          {(props) => <Input {...props} value={fullName} onChange={(event) => setFullName(event.target.value)} autoFocus />}
        </Field>

        <Field label={t('auth.phone')} hint={t('auth.phone.hint')} error={errors.phone} required>
          {(props) => <PhoneInput {...props} value={phone} onChange={(event) => setPhone(event.target.value)} />}
        </Field>

        <Field label={t('invitation.email')} hint={t('invitation.email.hint')}>
          {(props) => (
            <EmailInput
              {...props}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>

        <Field label={t('guardians.relationship')} required>
          {(props) => (
            <Select
              {...props}
              value={relationship}
              onChange={(event) => setRelationship(event.target.value as GuardianRelationship)}
            >
              {RELATIONSHIPS.map((value) => (
                <option key={value} value={value}>{t(`guardians.relationship.${value}`)}</option>
              ))}
            </Select>
          )}
        </Field>

        <HistoryFromField
          choice={historyChoice}
          onChoice={setHistoryChoice}
          date={historyDate}
          onDate={setHistoryDate}
          today={today()}
        />

        {/* Nothing else to set per guardian: every one sees the account, is told and may collect (Q42). */}
        <label className="dg-checkbox">
          <input type="checkbox" checked={isPrimary} onChange={(event) => setIsPrimary(event.target.checked)} />
          <span>{t('guardians.primary')}</span>
        </label>

        <Row>
          <Button type="submit" busy={add.isPending} busyLabel={t('action.saving')}>{t('guardians.add')}</Button>
          <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
        </Row>
      </Stack>
    </form>
  );
}

/**
 * One link's settings: how far back the guardian reads (Q28), whether they are primary, and suspending (Q30).
 * Unlinking ends the link; the record stays.
 */
function LinkEditor({ childId, enrollmentDate, link, onClose, onSaved }: {
  childId: number;
  enrollmentDate: string;
  link: GuardianLinkResponse;
  onClose: () => void;
  onSaved: () => void;
}) {
  const api = useApi();
  const { t, today } = useI18n();

  const [historyChoice, setHistoryChoice] = useState<HistoryChoice>('date');
  const [historyDate, setHistoryDate] = useState(link.historyVisibleFrom ?? '');
  const [isPrimary, setIsPrimary] = useState(link.isPrimary);
  const [suspended, setSuspended] = useState(link.accessLevel === 'Suspended');
  const [note, setNote] = useState(link.restrictionNote ?? '');
  const [evidence, setEvidence] = useState(link.restrictionEvidenceRef ?? '');
  const [noteError, setNoteError] = useState<string | undefined>();
  const [unlinking, setUnlinking] = useState(false);

  const save = useMutation({
    mutationFn: (body: UpdateGuardianLinkRequest) => api.guardians.update(childId, link.id, body),
    onSuccess: onSaved,
  });

  const unlink = useMutation({
    mutationFn: () => api.guardians.remove(childId, link.id),
    onSuccess: onSaved,
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (invalidDateIn(event.currentTarget)) return;

    const from = historyFrom(historyChoice, historyDate, enrollmentDate, today());
    if (!from) return;

    if (suspended && !note.trim()) {
      setNoteError(t('guardians.suspend.noteRequired'));
      return;
    }
    setNoteError(undefined);

    save.mutate({
      historyVisibleFrom: from,
      restrictionNote: note.trim(),
      restrictionEvidenceRef: evidence.trim(),
      accessLevel: suspended ? 'Suspended' : 'Full',
      // Only a change is sent. A suspended guardian cannot be primary; the server promotes someone else.
      isPrimary: !suspended && isPrimary !== link.isPrimary ? isPrimary : null,
    });
  }

  return (
    <form onSubmit={submit} noValidate className="dg-panel">
      <Stack>
        {save.isError && <ErrorState error={save.error} />}
        {unlink.isError && <ErrorState error={unlink.error} />}

        <HistoryFromField
          choice={historyChoice}
          onChoice={setHistoryChoice}
          date={historyDate}
          onDate={setHistoryDate}
          today={today()}
        />

        <label className="dg-checkbox">
          <input
            type="checkbox"
            checked={isPrimary && !suspended}
            disabled={suspended}
            onChange={(event) => setIsPrimary(event.target.checked)}
          />
          <span>{t('guardians.primary')}</span>
        </label>

        <label className="dg-checkbox">
          <input type="checkbox" checked={suspended} onChange={(event) => setSuspended(event.target.checked)} />
          <span>{t('guardians.suspend')}</span>
        </label>

        {suspended && (
          <>
            <Notice tone="warning">{t('guardians.suspend.hint')}</Notice>
            <Field label={t('guardians.suspend.note')} error={noteError} required>
              {(props) => <Textarea {...props} value={note} onChange={(event) => setNote(event.target.value)} />}
            </Field>
            <Field label={t('guardians.suspend.evidence')}>
              {(props) => <Input {...props} value={evidence} onChange={(event) => setEvidence(event.target.value)} />}
            </Field>
          </>
        )}

        <Row>
          <Button type="submit" busy={save.isPending} busyLabel={t('action.saving')}>{t('action.save')}</Button>
          <Button variant="ghost" onClick={onClose}>{t('action.cancel')}</Button>
        </Row>

        {unlinking ? (
          <>
            <Notice tone="warning">{t('guardians.unlink.hint')}</Notice>
            <Row>
              <Button variant="danger" busy={unlink.isPending} busyLabel={t('action.saving')} onClick={() => unlink.mutate()}>
                {t('guardians.unlink.confirm')}
              </Button>
              <Button variant="ghost" onClick={() => setUnlinking(false)}>{t('action.cancel')}</Button>
            </Row>
          </>
        ) : (
          <Row>
            <Button variant="danger" onClick={() => setUnlinking(true)}>{t('guardians.unlink')}</Button>
          </Row>
        )}
      </Stack>
    </form>
  );
}
